import React, {useCallback, useMemo, useState} from 'react';

import debounce from 'lodash.debounce';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';


import {
    inputAddorment,
    inputAddormentItem,
    inputAddormentItemClickable,
} from './input.module.styl';

type Props = {
    id: string,
    value: string,
    onChange: (value: string) => void,
    onClear: () => void,
    timeOut: number,

    width?: number,
    placeholder?: string,
    disabled?: boolean,
    maxLength?: number,
};

export const FilterInput = (props: Props) => {
    const {id, value, placeholder, onChange, disabled, width, onClear, timeOut, maxLength} = props;
    const [innerState, setInnerState] = useState<string>(value);

    useEffectWithDeps((prev, current) => {
        if (prev !== current) {
            setInnerState(current);
        }
    }, value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedChange = useCallback(debounce((newValue: string) => {
        onChange(newValue);
    }, timeOut), [onChange]);

    const onInput = useCallback<React.FormEventHandler<HTMLInputElement>>(event => {
        const newValue = event.currentTarget.value;
        if (newValue !== ' ') {
            setInnerState(newValue);
            debouncedChange(newValue);
        }
    }, [debouncedChange]);

    const endAdornment = useMemo(() => (
        <div className={inputAddorment}>
            {!!innerState && (
                <Icon
                    className={`${inputAddormentItem} ${inputAddormentItemClickable}`}
                    type={Icons.CLOSE}
                    size={Sizes.Size_19}
                    onClick={() => {
                        setInnerState('');
                        onClear();
                    }}
                />
            )}
            <Icon type={Icons.SEARCH} size="Size_14"/>
        </div>
    ), [innerState, onClear]);

    const onBlur = useCallback(() => {
        onChange(innerState);
        setInnerState(innerState);
    }, [innerState, onChange]);

    const onKeyUp = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    }, []);

    return (
        <div style={{width}}>
            <Input
                id={id}
                value={innerState}
                placeholder={placeholder}
                autoComplete="off"
                autoCorrect="off"
                onInput={onInput}
                disabled={disabled}
                endAdornment={endAdornment}
                onBlur={onBlur}
                onKeyUp={onKeyUp}
                maxLength={maxLength}
            />
        </div>
    );
};
